import { useState, type ReactNode } from "react";
import { SrIcon } from "@/components/ui/SrIcon";

interface Props {
  iconName: string;
  title: string;
  description: string;
  count: number;
  defaultOpen?: boolean;
  emptyText?: string;
  children: ReactNode;
}

/**
 * Akkordeon-seksjon for én innboks-kategori. Stil matcher MyTasksList:
 * border-[1.5px] border-foreground bg-card, font-display tittel,
 * lyse runde tellere med tynn border.
 */
export function InboxAccordion({
  iconName,
  title,
  description,
  count,
  defaultOpen = true,
  emptyText = "Ingen ventende.",
  children,
}: Props) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="border-[1.5px] border-foreground bg-card">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between p-4 hover:bg-muted/50 transition-colors"
        aria-expanded={open}
      >
        <div className="flex items-center gap-3">
          <SrIcon name={iconName} className="w-4 h-4 text-muted-foreground" />
          <span className="font-display font-semibold text-lg tracking-tight">{title}</span>
          <span className="inline-flex items-center text-[12px] px-2 py-0.5 rounded-full border-[1.5px] border-foreground bg-card font-medium tabular-nums">
            {count}
          </span>
          <span className="text-xs text-muted-foreground hidden sm:inline">
            · {description}
          </span>
        </div>
        <SrIcon
          name={open ? "chevron-up" : "chevron-down"}
          className="w-4 h-4 text-muted-foreground"
        />
      </button>
      {open && (
        <div className="border-t">
          {count === 0 ? (
            <div className="px-5 py-6 text-sm text-muted-foreground italic">
              {emptyText}
            </div>
          ) : (
            children
          )}
        </div>
      )}
    </div>
  );
}
